/**
 * 수업 생성 확인 창(설계서 P8·7-3)의 판정 — 7개 생성 경로가 함께 쓰는 순수 함수.
 *
 * 생성하려는 수업마다 "그 수업에 쓰일 수강권"과 "수업 시작 시각"을 넘기면,
 * 정지 구간 안에 드는 것만 수강권 단위로 묶어 돌려준다.
 * 이동·승인·승격·수강권 교체에는 쓰지 않는다(그 경로는 확인 창이 없다).
 */
import { getKstTodayKey, getOpenPause, OpenPause, PauseLike, previewResume, ResumePreview, isInPauseWindow } from './status';
import { PausePassRef } from './types';
import { getPassRefId } from './requestKey';

export type PauseGateTarget = {
    ref: PausePassRef;
    pause: PauseLike;
    /** 회원 이름(창에 표시) */
    memberName: string;
    /** 수강권 이름(창에 표시) */
    passName?: string;
    /** 이 생성에서 이 수강권으로 만들 수업들의 시작 시각 */
    startTimes: Array<string | Date | null | undefined>;
    /** 사용 중이 아니면(만료·취소) 판정하지 않는다. 모르면 true */
    inUse?: boolean;
    /** 만료일(미리보기용, 없으면 일수만 보여준다) */
    expiry?: string | null;
    /** 공유 수강권이면 소유자 이름 — 「풀고 등록」이 소유자 정지까지 푼다는 안내에 쓴다 */
    sharedOwnerName?: string | null;
    isShared?: boolean;
};

export type PauseGateHit = {
    key: string;
    ref: PausePassRef;
    open: OpenPause;
    memberNames: string[];
    passName: string;
    /** 정지 기간에 드는 수업 수 */
    hitCount: number;
    /** 아직 시작 전인 정지(해제하면 취소로 처리된다) */
    isScheduled: boolean;
    preview: ResumePreview | null;
    isShared: boolean;
    sharedOwnerName: string | null;
};

export const getPassKey = (ref: PausePassRef) => `${ref.kind}:${getPassRefId(ref)}`;

export const findPauseGateHits = (
    targets: PauseGateTarget[],
    todayKey: string = getKstTodayKey(),
): PauseGateHit[] => {
    const byPass = new Map<string, PauseGateHit>();
    for (const target of targets) {
        if (!target || target.inUse === false) continue;
        if (!getPassRefId(target.ref)) continue;
        const open = getOpenPause(target.pause);
        if (!open) continue;
        const hitCount = (target.startTimes || []).filter((t) => isInPauseWindow(target.pause, t)).length;
        if (hitCount === 0) continue;

        const key = getPassKey(target.ref);
        const existing = byPass.get(key);
        if (existing) {
            existing.hitCount += hitCount;
            if (target.memberName && !existing.memberNames.includes(target.memberName)) {
                existing.memberNames.push(target.memberName);
            }
            continue;
        }
        byPass.set(key, {
            key,
            ref: target.ref,
            open,
            memberNames: target.memberName ? [target.memberName] : [],
            passName: target.passName || '수강권',
            hitCount,
            isScheduled: todayKey < open.startKey,
            preview: previewResume(target.pause, target.expiry ?? null, todayKey),
            isShared: target.isShared === true || !!target.sharedOwnerName,
            sharedOwnerName: target.sharedOwnerName ? String(target.sharedOwnerName) : null,
        });
    }
    return Array.from(byPass.values());
};

/** 해제 실패·생성 실패 안내에 쓰는 이름 목록 */
export const formatHitNames = (hits: Pick<PauseGateHit, 'memberNames'>[]) =>
    hits
        .flatMap((h) => h.memberNames)
        .filter((name, i, arr) => !!name && arr.indexOf(name) === i)
        .map((name) => `${name}님`)
        .join(', ');

/**
 * 「풀고 등록」 뒤 생성이 실패했을 때 기존 실패 문구 앞에 붙이는 안내.
 * 정지가 풀렸다는 사실만 말한다. 생성 결과(부분 성공·결과 불명)와 재시도 지시는 기존 오류 분기의 문구에 맡긴다
 * (교차검증 10: "실패했어요. 다시 등록해 주세요"가 1:2 부분 생성 뒤 목록 확인 안내와 상반됐다).
 */
export const buildResumedThenFailedMessage = (resumedNames: string, original?: string) => {
    const head = `정지는 해제됐어요${resumedNames ? `(${resumedNames})` : ''}.`;
    const tail = String(original || '').trim();
    return tail ? `${head}\n\n${tail}` : head;
};

/**
 * PT 생성 화면의 수강권 행(생성용 회원 목록 + 공유 이용권 병합 결과) → 관문 대상.
 * 행 아이디는 요청 본문과 같은 규칙(`membership.id`, 없으면 `_id`)으로 뽑는다.
 */
export const toPtPauseGateTarget = (
    membership: any,
    memberName: string,
    startTimes: Array<string | Date | null | undefined>,
): PauseGateTarget | null => {
    const membershipId = String(membership?.id || membership?._id || '').trim();
    if (!membership || !membershipId) return null;
    const isShared = membership.isSharedMembership === true || !!membership.membershipSharePoolId;
    return {
        ref: { kind: 'pt', membershipId },
        pause: membership.pause,
        inUse: membership.status === undefined ? true : String(membership.status).toLowerCase() === 'active',
        memberName: memberName || '회원',
        passName: String(membership.name || '수강권'),
        startTimes,
        expiry: membership.expirationDate ?? null,
        isShared,
        sharedOwnerName: isShared ? membership.sharedOwner?.name || null : null,
    };
};

export const compactTargets = (targets: Array<PauseGateTarget | null | undefined>): PauseGateTarget[] =>
    targets.filter((t): t is PauseGateTarget => !!t);
